import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import { ArrowUpRight, Lock, KeyRound } from "lucide-react";
import { usePasskeysAvailable, signInWithPasskey } from "../lib/passkeys";

export default function SignIn() {
  const { signIn, setUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const passkeysAvailable = usePasskeysAvailable();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await signIn(email.trim(), password);
      const defaultPath =
        u.role === "admin"
          ? "/admin"
          : u.role === "caregiver"
          ? "/caregiver"
          : "/home";
      const from = loc.state?.from || defaultPath;
      nav(from, { replace: true });
    } catch (err) {
      toast(err.response?.data?.detail || "We couldn't sign you in just now.");
    } finally {
      setBusy(false);
    }
  };

  const useDemo = () => {
    setEmail("parent@orbit.app");
    setPassword("orbit-calm");
  };

  const passkeyLogin = async () => {
    if (!email.trim()) {
      toast("Enter your email first.");
      return;
    }
    setPkBusy(true);
    try {
      const u = await signInWithPasskey(email.trim());
      setUser(u);
      const defaultPath =
        u.role === "admin"
          ? "/admin"
          : u.role === "caregiver"
          ? "/caregiver"
          : "/home";
      nav(loc.state?.from || defaultPath, { replace: true });
    } catch {
      // toast already shown by helper
    } finally {
      setPkBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <aside
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/5b7539fb-c408-4336-acd4-f414390d8b8e/images/3d69b20457cef1981c6dc25b9bf3242927f7b147df03d0c9d51cd1775063c86f.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#FBF9F6]/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <span className="eyebrow text-[#2C2B29]/70">A quiet welcome</span>
          <p className="mt-6 font-serif text-3xl italic text-[#2C2B29] leading-snug max-w-md">
            &ldquo;The most luxurious thing technology can do is leave you
            calmer than it found you.&rdquo;
          </p>
        </div>
      </aside>

      <main className="flex flex-col">
        <div className="px-6 sm:px-12 py-8 flex items-center justify-between">
          <Link
            to="/"
            data-testid="signin-brand"
            className="flex items-center"
          >
            <Brand size="sm" dataTestId="brand-signin" />
          </Link>
          <span className="trust-pill">
            <Lock size={11} strokeWidth={1.5} />
            Encrypted
          </span>
        </div>

        <div className="flex-1 flex items-center px-6 sm:px-16">
          <div className="w-full max-w-md fade-up">
            <span className="eyebrow">Welcome back</span>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl leading-none tracking-tight">
              Sign in,
              <br />
              <em className="italic text-[#8A9984]">gently.</em>
            </h1>

            <form onSubmit={onSubmit} className="mt-12 space-y-8">
              <div>
                <label htmlFor="signin-email" className="eyebrow">{t("auth.email")}</label>
                <input
                  id="signin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="orbit-input mt-3"
                  placeholder="you@quiet.place"
                  data-testid="signin-email"
                />
              </div>
              <div>
                <label htmlFor="signin-password" className="eyebrow">{t("auth.password")}</label>
                <input
                  id="signin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="orbit-input mt-3"
                  placeholder="••••••"
                  data-testid="signin-password"
                />
              </div>

              <div className="pt-4 flex flex-wrap gap-3 items-center">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="signin-submit"
                  className="btn-primary"
                >
                  {busy ? t("auth.submit_pending") : t("auth.submit")}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={useDemo}
                  data-testid="signin-demo"
                  className="btn-ghost"
                >
                  {t("auth.demo_account")}
                </button>
              </div>
              <div className="pt-1 -mt-2">
                <Link
                  to="/forgot-password"
                  data-testid="signin-forgot-password"
                  className="text-xs text-[#6B7280] hover:text-[#1F3A2A] underline underline-offset-4 decoration-[#9BA995]"
                >
                  {t("auth.forgot_password")}
                </Link>
              </div>
            </form>

            {passkeysAvailable && email.trim().length > 0 && (
              <div className="mt-7 pt-7 border-t hairline">
                <p className="text-xs text-[#6A6863] tracking-wide mb-3">
                  Or, if you&rsquo;ve set up a passkey on this device:
                </p>
                <button
                  type="button"
                  onClick={passkeyLogin}
                  disabled={pkBusy || !email.trim()}
                  data-testid="signin-passkey"
                  className="btn-ghost"
                >
                  {pkBusy ? "Listening…" : "Sign in with a passkey"}
                  <KeyRound size={14} strokeWidth={1.5} />
                </button>
              </div>
            )}

            <p className="mt-10 text-sm text-[#6A6863]">
              New here?{" "}
              <Link
                to="/signup"
                data-testid="signin-link-signup"
                className="text-[#2C2B29] underline underline-offset-4 decoration-[#9BA995]"
              >
                Create your Alouvya
              </Link>
            </p>
          </div>
        </div>

        <div className="px-6 sm:px-12 py-8 text-xs text-[#6A6863] tracking-wide space-y-3">
          <p
            data-testid="signin-trust-tagline"
            data-manifesto-negation
            className="text-[11px] tracking-[0.16em] uppercase text-[#6A6863]"
          >
            Privacy-first. No tracking. Human-updated child status only.
          </p>
          <p>You own your data. Always.</p>
        </div>
      </main>
    </div>
  );
}
